/**
 * Produce a sanitized HTML snapshot of a DOM subtree suitable for shipping
 * to the LLM as "page context." Strips elements that contribute nothing to
 * understanding the page (scripts, styles, media, etc.), drops event
 * handlers and other unsafe attributes, and removes the chat container
 * itself so the model doesn't read its own past output as page content.
 *
 * Returns innerHTML of a clone — the original DOM is untouched.
 */
const TAGS_TO_REMOVE = [
  "script",
  "style",
  "link",
  "meta",
  "iframe",
  "noscript",
  "svg",
  "canvas",
  "video",
  "audio",
  "object",
  "embed",
  "param",
  "track",
  "source",
  "map",
  "area",
  "datalist",
  "progress",
  "meter",
  "details",
  "summary",
  "dialog",
  "template",
  "slot",
];

const ALLOWED_ATTRIBUTES = ["href", "src", "alt", "title"];

export function sanitizeHtmlForLLM(root: HTMLElement = document.body): string {
  const cloned = root.cloneNode(true) as HTMLElement;

  TAGS_TO_REMOVE.forEach(tag => {
    cloned.querySelectorAll(tag).forEach(el => el.remove());
  });

  // Strip the chat container so we don't feed the model its own past
  // messages as page content.
  const chatEl = cloned.querySelector('[data-cy="chat-messages"]');
  if (chatEl && chatEl.parentElement) {
    chatEl.parentElement.removeChild(chatEl);
  }

  const cleanElement = (el: Element) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const isAllowed =
        ALLOWED_ATTRIBUTES.includes(name) || name.startsWith("data-");
      const isEventAttribute = name.startsWith("on");
      if (!isAllowed || isEventAttribute) {
        el.removeAttribute(attr.name);
      }
    }
    Array.from(el.children).forEach(cleanElement);
  };

  Array.from(cloned.children).forEach(cleanElement);

  return cloned.innerHTML;
}
